import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import ShareScoreSheet from "@/components/common/share-score-sheet/share-score-sheet";
import type {
    ShareScoreSheetContextValue,
    ShareScoreSheetPayload,
    ShareScoreSheetType,
} from "@/components/common/share-score-sheet/share-score-sheet.types";
import { formatShareScoreText } from "@/helpers/formatShareScoreText";
import { triggerRedditShare } from "@/helpers/triggerRedditShare";
import { triggerShare } from "@/helpers/triggerShare";
import useLocalStorage from "@/hooks/useLocalStorage";
import useMixpanelEvent from "@/hooks/useMixpanelEvent";
import { useUser } from "@/context/UserContext";

const ShareScoreSheetContext = createContext<ShareScoreSheetContextValue | null>(null);

/**
 * Provides global share-score sheet state and actions for puzzle/classic surfaces.
 */
export function ShareScoreSheetProvider({ children }: { children: ReactNode }): ReactElement {
    const [isOpen, setIsOpen] = useState(false);
    const [enableShareWithLink, setEnableShareWithLink] = useState(true);
    const activePayloadRef = useRef<ShareScoreSheetPayload | null>(null);
    const { userState } = useUser();
    const { isAuthenticated } = useAuth0();
    const [visitorStatus] = useLocalStorage<string>("visitor-status", "");
    const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();
    const isRegistered = Boolean(userState?.id && isAuthenticated);

    const trackOpenShareScoreSheet = useCallback(
        (sharePayload: ShareScoreSheetPayload): void => {
            sendMixpanelEvent("Open Share Score Sheet", {
                isAuthenticated: isRegistered,
                visitorStatus: isRegistered ? null : visitorStatus || "new-guest",
                pageName: sharePayload.pageName,
                propertyUrl: sharePayload.propertyUrl,
                email: userState?.emailAddress || null,
                nickname: userState?.nickname || null,
                location: sharePayload.location,
                userScore: sharePayload.userScore,
            });
        },
        [
            isRegistered,
            sendMixpanelEvent,
            userState?.emailAddress,
            userState?.nickname,
            visitorStatus,
        ],
    );

    const trackShareScore = useCallback(
        (sharePayload: ShareScoreSheetPayload, shareType: ShareScoreSheetType): void => {
            sendMixpanelEvent("Share Score", {
                isAuthenticated: isRegistered,
                visitorStatus: isRegistered ? null : visitorStatus || "new-guest",
                pageName: sharePayload.pageName,
                propertyUrl: sharePayload.propertyUrl,
                email: userState?.emailAddress || null,
                nickname: userState?.nickname || null,
                location: sharePayload.location,
                type: shareType,
                userScore: sharePayload.userScore,
            });
        },
        [
            isRegistered,
            sendMixpanelEvent,
            userState?.emailAddress,
            userState?.nickname,
            visitorStatus,
        ],
    );

    const openShareScoreSheet = useCallback(
        (sharePayload: ShareScoreSheetPayload): void => {
            activePayloadRef.current = sharePayload;
            setEnableShareWithLink(sharePayload.enableShareWithLink !== false);
            setIsOpen(true);
            trackOpenShareScoreSheet(sharePayload);
        },
        [trackOpenShareScoreSheet],
    );

    const closeShareScoreSheet = useCallback((): void => {
        setIsOpen(false);
        activePayloadRef.current = null;
        setEnableShareWithLink(true);
    }, []);

    const executeShare = useCallback(
        async (shareType: ShareScoreSheetType): Promise<void> => {
            const payload = activePayloadRef.current;
            if (!payload) {
                return;
            }
            const includeLink = shareType === "Grid+Link";
            const formatted = formatShareScoreText({
                shareTextTitle: payload.shareTextTitle,
                shareTextBody: payload.shareTextBody,
                shareUrl: payload.shareUrl,
                streak: payload.streak,
                isRegistered,
                includeLink,
            });
            trackShareScore(payload, shareType);
            if (shareType === "Reddit") {
                const redditFormatted = formatShareScoreText({
                    shareTextTitle: payload.shareTextTitle,
                    shareTextBody: payload.shareTextBody,
                    shareUrl: payload.shareUrl,
                    streak: payload.streak,
                    isRegistered,
                    includeLink: false,
                });
                await triggerRedditShare(redditFormatted.mobileTitle, redditFormatted.mobileText);
                return;
            }
            await triggerShare({
                web: { text: formatted.webText },
                mobile: {
                    title: formatted.mobileTitle,
                    text: formatted.mobileText,
                    url: formatted.mobileUrl,
                },
                skipToast: false,
            });
        },
        [isRegistered, trackShareScore],
    );

    const contextValue = useMemo(
        (): ShareScoreSheetContextValue => ({
            openShareScoreSheet,
        }),
        [openShareScoreSheet],
    );

    return (
        <ShareScoreSheetContext.Provider value={contextValue}>
            {children}
            <ShareScoreSheet
                open={isOpen}
                enableShareWithLink={enableShareWithLink}
                onClose={closeShareScoreSheet}
                onShareGridOnly={() => void executeShare("Grid Only")}
                onShareGridWithLink={() => void executeShare("Grid+Link")}
                onShareReddit={() => void executeShare("Reddit")}
            />
        </ShareScoreSheetContext.Provider>
    );
}

/**
 * Opens the global share score sheet; must be used within ShareScoreSheetProvider.
 */
export function useShareScoreSheet(): ShareScoreSheetContextValue {
    const context = useContext(ShareScoreSheetContext);
    if (!context) {
        throw new Error("useShareScoreSheet must be used within ShareScoreSheetProvider");
    }
    return context;
}
